import React, { useState } from "react";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faCircle,
  faUserShield,
  faClock,
  faFileExport,
  faUserCheck,
  faExclamationTriangle,
  faBookOpen, 
  faLightbulb, 
  faTimes
} from "@fortawesome/free-solid-svg-icons";
import { Chart as ChartJS, ArcElement, Tooltip, Legend } from "chart.js";
import { Pie } from "react-chartjs-2";

ChartJS.register(ArcElement, Tooltip, Legend);

export default function RTDHome() {
  const [statusModalOpen, setStatusModalOpen] = useState(false);
  const [overrideModalOpen, setOverrideModalOpen] = useState(false);
  const [showBill, setShowBill] = useState(false);
  const [showSolution, setShowSolution] = useState(false);
  const [currentStatus, setCurrentStatus] = useState("Available");
  const [visibility, setVisibility] = useState(["Team"]);
  const [auditLogs] = useState([
    { id: 1, user: "Arjun Oraon", action: "Set Offline", time: "10:15 AM" },
    { id: 2, user: "Satyvrat Sharma", action: "Override Request", time: "11:00 AM" },
    { id: 3, user: "Shanti Lad", action: "Set Do Not Disturb", time: "11:30 AM" },
  ]);

  const statusOptions = [
    { label: "Available", icon: faCircle, color: "text-green-400" },
    { label: "Do Not Disturb", icon: faCircle, color: "text-red-500" },
    { label: "Offline", icon: faCircle, color: "text-gray-400" },
    { label: "On Break", icon: faClock, color: "text-yellow-400" },
  ];

  const visibilityOptions = ["Team", "Manager", "Selected People"];

  const chartData = {
    labels: ["Overrides", "Normal Status", "Grievances"],
    datasets: [
      {
        label: "# of Actions",
        data: [3, 7, 2],
        backgroundColor: ["#b91c1c", "#7c2d12", "#f59e0b"],
        borderWidth: 1,
      },
    ],
  };

  const complianceText = `
    Think5Whys RTD Compliance Solution helps organizations implement India's Right to Disconnect policies seamlessly.
    - Employees can update status anytime during office hours.
    - Selective visibility ensures privacy and control.
    - Auto-replies maintain communication etiquette.
    - Audit export (JSON & PDF) provides legal evidence.
    - Metrics and dashboards help HR and managers track compliance.
    `;

  // Text for Right to Disconnect 2025 Bill
  const billText = `
    Right to Disconnect Bill, 2025 (India) - 5W1H:
    WHAT: Legal right for employees to disconnect from work communications outside office hours.
    WHO: Introduced by MP Supriya Sule; applies to employees & employers nationwide.
    WHEN: Introduced December 2025, currently private member's bill.
    WHERE: Nationwide, across all sectors.
    WHY: Protect work-life balance, reduce burnout, promote mental health.
    HOW: Legal protection, penalties for violations, welfare authority, overtime pay, mutual emergency rules.
    `;

  return (
    <div className="min-h-screen bg-black text-white p-4">
      <div className="mx-auto grid gap-2 place-items-center">
        <div className="border-b border-blue-400">
            <h1 className="text-lg lg:text-xl font-bold text-teal-200 mb-2">
            📴 Right to Disconnect Dashboard by Think5Whys
            </h1>
        </div>
        <div className="grid grid-cols-2 gap-1 mb-2 place-items-center">
          <button
            onClick={() => setShowBill(!showBill)}
            className="bg-blue-600 hover:bg-blue-700 text-xs lg:text-sm w-48 px-2 py-2 rounded-md flex justify-center gap-2 text-center items-center"
          >
            <FontAwesomeIcon icon={faBookOpen} /> RTD Bill 2025
          </button>
          <button
            onClick={() => setShowSolution(!showSolution)}
            className="bg-teal-600 hover:bg-teal-700 text-xs lg:text-sm w-48 px-2 py-2 rounded-md flex justify-center items-center gap-2 text-center"
          >
            <FontAwesomeIcon icon={faLightbulb} /> Solution
          </button>
        </div>

        {showBill && (
            <div className="relative bg-gray-900 p-4 rounded-md shadow-md w-full max-w-md mx-auto">
                <button
                onClick={() => setShowBill(false)}
                className="absolute top-2 right-2 text-red-400 hover:text-white text-sm p-1"
                aria-label="Close"
                >
                <FontAwesomeIcon icon={faTimes} />
                </button>

                <h2 className="text-sm lg:text-lg font-bold mb-2 text-yellow-200 flex items-center gap-1">
                📴 Right to Disconnect (RTD) Bill 2025
                </h2>

                <p className="text-xs lg:text-sm whitespace-pre-line">{billText}</p>
            </div>
        )}

        {showSolution && (
            <div className="relative bg-gray-900 p-4 rounded-md shadow-md w-full max-w-md mx-auto">
                <button
                onClick={() => setShowSolution(false)}
                className="absolute top-2 right-2 text-red-400 hover:text-white text-sm p-1 justify-end items-end"
                aria-label="Close"
                >
                <FontAwesomeIcon icon={faTimes} />
                </button>
                <h2 className="text-sm lg:text-lg font-bold mb-2 text-yellow-200">Think5Whys RTD Compliance Solution</h2>
                <p className="text-sm whitespace-pre-line">{complianceText}</p>
          </div>
        )}

        {/* Status Section */}
        <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md shadow-md">
          <h2 className="text-sm lg:text-lg font-semibold mb-2 flex items-center gap-2">
            <FontAwesomeIcon icon={faUserCheck} /> My Status
          </h2>
          <p className="mb-4 text-xs lg:text-sm">
            Current Status:{" "}
            <span className="font-bold text-green-400">{currentStatus}</span>
          </p>
          <button
            onClick={() => setStatusModalOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 px-4 py-2 w-40 rounded-md text-xs lg:text-sm"
          >
            Change Status
          </button>
        </div>

        {/* Manager Override Section */}
        <div className="bg-gray-900 rounded-xl p-6 w-full max-w-md shadow-md">
          <h2 className="text-sm lg:text-lg font-semibold mb-2 flex items-center gap-2">
            <FontAwesomeIcon icon={faUserShield} /> Manager Override
          </h2>
          <button
            onClick={() => setOverrideModalOpen(true)}
            className="bg-red-700 hover:bg-red-800 px-4 py-2 w-40 rounded-md text-xs lg:text-sm"
          >
            Request Override
          </button>
        </div>

        {/* HR Evidence Section */}
        <div className="bg-gray-900 rounded-xl p-4 w-full max-w-md shadow-md">
            {/* Header */}
            <h2 className="text-sm lg:text-lg font-semibold mb-3 flex items-center gap-2">
                <FontAwesomeIcon icon={faFileExport} /> HR Audit Logs
            </h2>

            {/* Audit Log List */}
            <ul className="mb-3 max-h-32 overflow-y-auto text-sm">
                {auditLogs.map((log) => (
                <li
                    key={log.id}
                    className="flex justify-between py-1 border-b border-gray-700"
                >
                    <span className="text-xs lg:text-sm">{log.user}</span>
                    <span className="text-gray-400 text-xs lg:text-sm">{log.action}</span>
                    <span className="text-gray-500 text-xs lg:text-sm">{log.time}</span>
                </li>
                ))}
            </ul>

            {/* Chart */}
            <div className="w-full flex justify-center">
                <div className="w-48 h-48 lg:w-64 lg:h-64">
                <Pie data={chartData} options={{ maintainAspectRatio: false }} />
                </div>
            </div>
            </div>
      </div>

      {/* Employee Status Modal */}
      {statusModalOpen && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black bg-opacity-70">
          <div className="bg-gray-900 rounded-xl p-6 w-11/12 max-w-md">
            <h2 className="text-xl font-bold mb-4">Change My Status</h2>
            {statusOptions.map((status) => (
              <button
                key={status.label}
                onClick={() => setCurrentStatus(status.label)}
                className={`flex items-center gap-2 w-40 mb-2 px-3 py-2 rounded-md hover:bg-gray-800 ${
                  currentStatus === status.label ? "bg-gray-700" : ""
                }`}
              >
                <FontAwesomeIcon icon={status.icon} className={status.color} />
                {status.label}
              </button>
            ))}

            <div className="mt-4">
              <label className="block mb-2 font-semibold">Visibility:</label>
              {visibilityOptions.map((v) => (
                <div key={v} className="mb-1">
                  <input
                    type="checkbox"
                    checked={visibility.includes(v)}
                    onChange={(e) => {
                      if (e.target.checked) {
                        setVisibility([...visibility, v]);
                      } else {
                        setVisibility(
                          visibility.filter((item) => item !== v)
                        );
                      }
                    }}
                  />{" "}
                  {v}
                </div>
              ))}
            </div>

            <button
              onClick={() => setStatusModalOpen(false)}
              className="mt-4 bg-green-600 hover:bg-green-700 px-4 py-2 rounded-md w-20"
            >
              Save
            </button>
          </div>
        </div>
      )}

      {/* Manager Override Modal */}
      {overrideModalOpen && (
        <div className="fixed inset-0 z-50 flex items-start justify-center bg-black bg-opacity-70 pt-16 pb-16 overflow-auto">
            <div className="bg-gray-900 rounded-xl shadow-xl w-11/12 max-w-md overflow-hidden">
            {/* Header */}
            <div className="flex justify-between items-center bg-gray-800 px-4 py-3 border-b border-gray-700">
                <h2 className="text-lg font-bold flex items-center gap-2 text-white">
                <FontAwesomeIcon icon={faExclamationTriangle} /> Request Override
                </h2>
                <button
                onClick={() => setOverrideModalOpen(false)}
                className="text-gray-400 hover:text-white text-xl w-10 font-bold"
                >
                &times;
                </button>
            </div>

            {/* Body */}
            <div className="p-4">
                <label className="block mb-2 font-semibold text-white">
                Reason / Justification:
                </label>
                <textarea
                className="w-full bg-gray-800 text-white rounded-md p-2 mb-4 resize-none focus:outline-none focus:ring-2 focus:ring-teal-500 h-24"
                placeholder="Explain why you need to override this status..."
                ></textarea>

                <button
                onClick={() => setOverrideModalOpen(false)}
                className="bg-red-700 hover:bg-red-800 text-xs lg:text-sm w-28 lg:w-40 px-4 py-2 rounded-md font-semibold"
                >
                Submit Override
                </button>
            </div>
            </div>
        </div>
        )}
    </div>
  );
}
